import { Rng } from '../core/rng';
import { STEP_MS } from '../core/loop';
import { GAME_HEIGHT, GAME_WIDTH } from '../render/viewport';

/**
 * Phase 0 placeholder: a flowing river and the title. It proves the
 * loop/render/deploy pipeline and gets replaced by real gameplay in Phase 1.
 */

const BANK_WIDTH = 34;
const CURRENT_SPEED = 90; // game units per second, downstream

interface Streak {
  x: number;
  y: number;
  length: number;
  alpha: number;
}

export class RiverPlaceholder {
  private streaks: Streak[] = [];
  private time = 0;

  constructor(seed: number) {
    const rng = new Rng(seed);
    for (let i = 0; i < 40; i++) {
      this.streaks.push({
        x: rng.range(BANK_WIDTH + 8, GAME_WIDTH - BANK_WIDTH - 8),
        y: rng.range(0, GAME_HEIGHT),
        length: rng.range(10, 28),
        alpha: rng.range(0.15, 0.45),
      });
    }
  }

  step(): void {
    const dt = STEP_MS / 1000;
    this.time += dt;
    for (const s of this.streaks) {
      s.y += CURRENT_SPEED * dt * (0.6 + s.alpha);
      if (s.y - s.length > GAME_HEIGHT) s.y -= GAME_HEIGHT + s.length * 2;
    }
  }

  render(ctx: CanvasRenderingContext2D): void {
    const water = ctx.createLinearGradient(0, 0, 0, GAME_HEIGHT);
    water.addColorStop(0, '#7fd6e8');
    water.addColorStop(1, '#4fb3d4');
    ctx.fillStyle = water;
    ctx.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    ctx.strokeStyle = '#ffffff';
    ctx.lineCap = 'round';
    ctx.lineWidth = 2;
    for (const s of this.streaks) {
      ctx.globalAlpha = s.alpha;
      ctx.beginPath();
      ctx.moveTo(s.x, s.y);
      ctx.lineTo(s.x, s.y - s.length);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;

    this.drawBank(ctx, 0, 1);
    this.drawBank(ctx, GAME_WIDTH, -1);
    this.drawTitle(ctx);
  }

  private drawBank(ctx: CanvasRenderingContext2D, edgeX: number, dir: 1 | -1): void {
    ctx.fillStyle = '#8ed17a';
    ctx.beginPath();
    ctx.moveTo(edgeX, 0);
    for (let y = 0; y <= GAME_HEIGHT; y += 16) {
      const wobble = Math.sin((y + this.time * CURRENT_SPEED) * 0.05) * 4;
      ctx.lineTo(edgeX + dir * (BANK_WIDTH + wobble), y);
    }
    ctx.lineTo(edgeX, GAME_HEIGHT);
    ctx.closePath();
    ctx.fill();
  }

  private drawTitle(ctx: CanvasRenderingContext2D): void {
    const bob = Math.sin(this.time * 2) * 4;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = '700 44px system-ui, sans-serif';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 8;
    ctx.strokeStyle = '#5a3a2a';
    ctx.strokeText('kawaus.io', GAME_WIDTH / 2, 260 + bob);
    ctx.fillStyle = '#fff4e6';
    ctx.fillText('kawaus.io', GAME_WIDTH / 2, 260 + bob);

    ctx.font = '600 16px system-ui, sans-serif';
    ctx.fillStyle = '#ffffff';
    ctx.fillText('an otter swims upstream…', GAME_WIDTH / 2, 310 + bob);
  }
}
